import { useRef } from 'react';

import Box from '@zenbusiness/zui/Box';
import DialogContent from '@zenbusiness/zui/DialogContent';
import Heading from '@zenbusiness/zui/Heading';
import Link from '@zenbusiness/zui/Link';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Paper from '@zenbusiness/zui/Paper';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import { styled } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import { FocusGrid, ContainerGrid, ActionButtonsGrid } from '#client/components/Grid';
import { ACTION_BUTTON_TYPE } from '#client/components/Grid/constants';
import ErrorDetail from '#client/components/MoveMoney/components/SummaryFormComponents/ErrorDetail';
import { convertCentToUSDText, isChaseBank } from '#client/components/MoveMoney/utils/common';
import { FAQ_OVERVIEW_URL } from '#client/constants/FAQs';
import useDepositContext from '#client/hooks/useDepositContext';
import useFormStep from '#client/hooks/useFormStep';

import { DialogContentStyle, PrintedBoxStyle, SummaryFormWrapper } from './Common.styles';

const DialogContentStyled = styled(DialogContent)(DialogContentStyle);
const Wrapper = styled(Stack)(SummaryFormWrapper);
const PrintedBox = styled(Box)(PrintedBoxStyle);

const failedIcon = (
  <SharedAsset
    alt=""
    height={50}
    src="/icon/micro/x.svg"
    width={50}
  />
);

export default function ErrorForm({
  title,
  errorTitle,
  errorDetail,
  handleClose,
  handleClosePreviousModal
}) {
  const contentRef = useRef(null);
  const { data } = useFormStep();
  const { selectedFromAccount, amount } = data;
  const { clearDepositData } = useDepositContext();
  const fromName = selectedFromAccount?.name ?? '';
  const fromBank = selectedFromAccount?.bank ?? '';
  const amountInUSD = convertCentToUSDText(amount);
  const accountNumber = selectedFromAccount?.accountNumber;
  const isHideEyeBtn = isChaseBank({
    bank: fromBank,
    connectedType: selectedFromAccount?.connectedType
  });
  const fullAccountNumber = isHideEyeBtn ? '' : selectedFromAccount?.fullAccountNumber;

  const handleTryAgain = () => {
    // Deposit must be cleared deposit data before trying again
    clearDepositData();
    handleClose();
    handleClosePreviousModal();
  };

  return (
    <>
      <DialogContentStyled>
        <ContainerGrid>
          <FocusGrid>
            <Wrapper role="form">
              <PrintedBox ref={contentRef}>
                <Stack
                  alignItems="center"
                  flexDirection={{ xs: 'column', lg: 'row' }}
                  gap="8px"
                  justifyContent="center"
                  paddingBottom={{ xs: '32px', lg: '40px' }}
                >
                  {failedIcon}
                  <Heading
                    align="center"
                    size="3xl"
                    weight="bold"
                  >
                    {title}
                  </Heading>
                </Stack>
                <Paper variant="outlined">
                  <ErrorDetail
                    accountHolder={fromName}
                    accountNumber={accountNumber}
                    amount={amountInUSD}
                    bank={fromBank}
                    fullAccountNumber={fullAccountNumber}
                    subTitle={errorDetail}
                    title={errorTitle}
                  />
                </Paper>
              </PrintedBox>
              <Box mt={{ xs: '32px', lg: '40px' }}>
                <Text size="sm">
                  Need more help?{' '}
                  <Link
                    href={FAQ_OVERVIEW_URL}
                    size="sm"
                    target="_blank"
                    underline="always"
                  >
                    FAQs
                  </Link>
                </Text>
              </Box>
            </Wrapper>
          </FocusGrid>
        </ContainerGrid>
      </DialogContentStyled>
      <ActionButtonsGrid type={ACTION_BUTTON_TYPE.TERTIARY}>
        <LoadingButton
          color="primary"
          fullWidth
          onClick={handleTryAgain}
          size="large"
          variant="contained"
        >
          Try again
        </LoadingButton>
      </ActionButtonsGrid>
    </>
  );
}

ErrorForm.propTypes = {
  title: PropTypes.string.isRequired,
  errorTitle: PropTypes.string.isRequired,
  errorDetail: PropTypes.string.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleClosePreviousModal: PropTypes.func.isRequired
};
